import { makeAutoObservable, runInAction } from 'mobx'

// evertel
import { decorate, injectable, inject } from '@evertel/di'
import { Api } from '@evertel/api'
import { APIDataAppVersion } from '@evertel/types'


import { LocalAsyncStorage } from '@evertel/utils'

interface DismissalRecord {
    noticeId: number
    dismissedAt: string
    priority: string
}

const DISMISSAL_KEY_PREFIX = 'appVersionNotice_'
const DISMISSAL_EXPIRY_MS: Record<string, number> = {
    info: 7 * 24 * 60 * 60 * 1000,         // 7 days
    recommended: 24 * 60 * 60 * 1000        // 24 hours
    // urgent: session only — handled by not persisting
}

class AppVersionService {
    notice: APIDataAppVersion | null = null
    shouldShowModal = false
    showBanner = false

    private hasChecked = false

    constructor(
        private api: Api,
        private localStorage: LocalAsyncStorage
    ) {
        makeAutoObservable(this)
    }

    /**
     * Check the API for a matching update notice and determine whether
     * to show the modal. Only runs once per app session (hasChecked guard).
     *
     * Dismissal rules:
     * - info: persisted, re-shows after 7 days
     * - recommended: persisted, re-shows after 24 hours
     * - urgent: not persisted, re-shows every app launch (session-only via hasChecked)
     */
    checkForUpdate = async () => {
        if (this.hasChecked) return
        this.hasChecked = true

        try {
            const result = await this.api.Routes.AppVersion.getCheck()

            if (!result) {
                runInAction(() => {
                    this.notice = null
                    this.shouldShowModal = false
                })
                return
            }

            const notice = result as APIDataAppVersion

            // Urgent notices always show (session-only dismissal handled by hasChecked)
            // Info/recommended check persisted dismissal
            const isDismissed = notice.priority !== 'urgent'
                ? await this.isDismissed(notice)
                : false

            runInAction(() => {
                this.notice = notice
                this.shouldShowModal = !isDismissed
            })
        } catch (error) {
            // Silently skip — never prevent app usage
            console.warn('AppVersionService: check failed', error)
            runInAction(() => {
                this.notice = null
                this.shouldShowModal = false
            })
        }
    }

    /**
     * Dismiss the current notice. For info/recommended, persists to storage
     * so the dismissal survives app restarts. For urgent, only sets the
     * in-memory flag (won't persist — next launch will re-show).
     */
    dismiss = async () => {
        if (!this.notice) return

        // Only persist dismissal for info/recommended
        if (this.notice.priority !== 'urgent') {
            const record: DismissalRecord = {
                noticeId: this.notice.id,
                dismissedAt: new Date().toISOString(),
                priority: this.notice.priority
            }

            try {
                await this.localStorage.setObject(
                    `${DISMISSAL_KEY_PREFIX}${this.notice.id}`,
                    record
                )
            } catch (error) {
                console.warn('AppVersionService: failed to persist dismissal', error)
            }
        }

        runInAction(() => {
            this.shouldShowModal = false
            if (this.notice?.priority === 'urgent') {
                this.showBanner = true
            }
        })
    }

    private isDismissed = async (notice: APIDataAppVersion): Promise<boolean> => {
        try {
            const record = await this.localStorage.getObject<DismissalRecord>(
                `${DISMISSAL_KEY_PREFIX}${notice.id}`,
                null
            )

            if (!record) return false

            const expiryMs = DISMISSAL_EXPIRY_MS[notice.priority]
            if (!expiryMs) return false

            const dismissedAt = new Date(record.dismissedAt).getTime()
            return (Date.now() - dismissedAt) < expiryMs
        } catch {
            return false
        }
    }
}

let i = 0
decorate(injectable(), AppVersionService)
decorate(inject(Api), AppVersionService, i++)
decorate(inject(LocalAsyncStorage), AppVersionService, i++)

export { AppVersionService }
